// Plan 06-05 Task 1 — Shell + hash routing 5 telas (D-138 + D-140).
//
// Padrão paridade com web-recurso/App.tsx: SessionProvider + parseHashRoute +
// hashchange listener. Routes: #login | #dashboard | #cofre | #members | #audit.
//
// D-140 — RoleGuard client-side: defesa em profundidade (server middleware é
// fonte da verdade). cofre_holder vê /cofre; admin_ti vê /members + /audit;
// dashboard requer qualquer um dos dois.

import { useEffect, useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { SessionProvider, useSession } from './contexts/SessionContext';
import QRCode from 'qrcode';
import {
  logout,
  getStoredSession,
  acceptPolicy,
  changePassword,
  activateFirstAccess,
} from './lib/auth-client';
import { LGP_02_VERSION } from '@aions/core';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { CofrePage } from './pages/CofrePage';
import { MembersPage } from './pages/MembersPage';
import { AuditPage } from './pages/AuditPage';
import { SubstituicoesPage } from './pages/SubstituicoesPage';
import { RoleGuard } from './components/RoleGuard';
import {
  Brand,
  Button,
  ErrorBoundary,
  PolicyAcceptanceModal,
  ChangePasswordForm,
  CHANGE_PASSWORD_MIN_LENGTH,
  FirstAccessForm,
  FIRST_ACCESS_MIN_LENGTH,
  SessionCountdown,
} from '@aions/ui-primitives';

// DEV-ONLY: "visão de Deus" (Fatia 2.5). O import só é referenciado quando
// import.meta.env.DEV é true → em prod a condição é literal `false`, o dynamic
// import vira dead-code e o Rollup NÃO emite o chunk. Sem flag de runtime.
const DevInspectPage = import.meta.env.DEV
  ? lazy(() => import('./pages/DevInspectPage').then((m) => ({ default: m.DevInspectPage })))
  : null;

type Route =
  | { kind: 'login' }
  | { kind: 'dashboard' }
  | { kind: 'cofre' }
  | { kind: 'members' }
  | { kind: 'audit' }
  | { kind: 'substituicoes' }
  | { kind: 'change-password' }
  | { kind: 'dev-inspect' }
  | { kind: 'first-access'; token: string; email: string; otpAuthUri: string | undefined };

function parseHashRoute(): Route {
  const raw = window.location.hash.replace(/^#\/?/, '');
  // first-access é PRÉ-AUTH e traz query (#first-access?token=…&email=…&otp=…).
  if (raw.startsWith('first-access')) {
    const qIdx = raw.indexOf('?');
    if (qIdx !== -1) {
      const params = new URLSearchParams(raw.slice(qIdx + 1));
      const token = params.get('token');
      const email = params.get('email');
      const otp = params.get('otp');
      if (token && email) {
        return { kind: 'first-access', token, email, otpAuthUri: otp === null ? undefined : otp };
      }
    }
    return { kind: 'login' };
  }
  if (raw === 'cofre') return { kind: 'cofre' };
  if (raw === 'members') return { kind: 'members' };
  if (raw === 'audit') return { kind: 'audit' };
  if (raw === 'substituicoes') return { kind: 'substituicoes' };
  if (raw === 'change-password') return { kind: 'change-password' };
  // DEV-ONLY: em prod import.meta.env.DEV é false → #dev-inspect cai no default.
  if (import.meta.env.DEV && raw === 'dev-inspect') return { kind: 'dev-inspect' };
  if (raw === '' || raw === 'dashboard') return { kind: 'dashboard' };
  return { kind: 'login' };
}

function setHash(route: Route): void {
  switch (route.kind) {
    case 'login':
      window.location.hash = '';
      break;
    case 'dashboard':
      window.location.hash = '#dashboard';
      break;
    case 'cofre':
      window.location.hash = '#cofre';
      break;
    case 'members':
      window.location.hash = '#members';
      break;
    case 'audit':
      window.location.hash = '#audit';
      break;
    case 'substituicoes':
      window.location.hash = '#substituicoes';
      break;
    case 'change-password':
      window.location.hash = '#change-password';
      break;
    case 'dev-inspect':
      window.location.hash = '#dev-inspect';
      break;
    case 'first-access':
      // Entrada via link externo — nunca navegamos programaticamente para cá.
      break;
  }
}

function AppShell(): JSX.Element {
  const { t } = useTranslation();
  const { session, setSession } = useSession();

  const [route, setRoute] = useState<Route>(() => {
    const initial = parseHashRoute();
    // first-access é pré-auth: respeita mesmo sem sessão.
    if (initial.kind === 'first-access') return initial;
    if (!session) return { kind: 'login' };
    return initial;
  });

  // Listener de hashchange sempre ativo (cobre navegação pré-auth para first-access).
  useEffect(() => {
    const onHash = (): void => {
      setRoute(parseHashRoute());
    };
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
    };
  }, []);

  // Sessão caiu (logout): volta para login, exceto se estiver no fluxo pré-auth.
  useEffect(() => {
    if (!session) {
      setRoute((r) => (r.kind === 'first-access' ? r : { kind: 'login' }));
    }
  }, [session]);

  const goto = (next: Route): void => {
    setRoute(next);
    setHash(next);
  };

  const handleLogout = async (): Promise<void> => {
    await logout();
    setSession(null);
    setRoute({ kind: 'login' });
    window.location.hash = '';
  };

  // first-access: tela pré-auth standalone (sem header/nav), antes do gate de sessão.
  if (route.kind === 'first-access') {
    return (
      <div className="app">
        <a className="skip-to-main" href="#main-content">
          {t('app.skip_to_main')}
        </a>
        <main id="main-content" className="app-main" tabIndex={-1}>
          <FirstAccessForm
            email={route.email}
            otpAuthUri={route.otpAuthUri}
            minLength={FIRST_ACCESS_MIN_LENGTH}
            devDefaultTotp={import.meta.env.MODE === 'development' ? '000000' : undefined}
            makeQrSvg={(uri) => QRCode.toString(uri, { type: 'svg', margin: 1 })}
            labels={{
              title: t('first_access.title'),
              intro: t('first_access.intro'),
              emailPrefix: t('first_access.email_prefix'),
              qrUnavailable: t('first_access.qr_unavailable'),
              qrNotProvided: t('first_access.qr_not_provided'),
              otpCopyLabel: t('first_access.otp_copy_label'),
              passwordLabel: t('first_access.password_label', { min: FIRST_ACCESS_MIN_LENGTH }),
              confirmLabel: t('first_access.confirm_label'),
              totpLabel: t('first_access.totp_label'),
              submit: t('first_access.submit'),
              submitting: t('first_access.submitting'),
              errTooShort: t('first_access.errors.too_short', { min: FIRST_ACCESS_MIN_LENGTH }),
              errMismatch: t('first_access.errors.mismatch'),
              errInvalidTotp: t('first_access.errors.invalid_totp'),
              errTotpIncorrect: t('first_access.errors.totp_incorrect'),
              errAlreadyActivated: t('first_access.errors.already_activated'),
              errNetwork: t('first_access.errors.network'),
              errGeneric: t('first_access.errors.generic'),
            }}
            onSubmit={async (password, totpCode) => {
              const res = await activateFirstAccess(route.token, route.email, password, totpCode);
              if (res.ok) return { ok: true };
              if (res.error === 'invalid_totp') return { ok: false, error: 'invalid_totp' };
              if (res.error === 'already_activated')
                return { ok: false, error: 'already_activated' };
              if (res.error === 'network') return { ok: false, error: 'network' };
              return { ok: false, error: 'other' };
            }}
            onSuccess={() => {
              window.location.hash = '';
              setRoute({ kind: 'login' });
            }}
          />
        </main>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="app">
        <a className="skip-to-main" href="#main-content">
          {t('app.skip_to_main')}
        </a>
        <main id="main-content" className="app-main" tabIndex={-1}>
          <LoginPage onSuccess={() => goto({ kind: 'dashboard' })} />
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <a className="skip-to-main" href="#main-content">
        {t('app.skip_to_main')}
      </a>
      <header role="banner" className="app-header">
        <Brand size="md" tagline={t('app.header_tagline')} />
        {/* Contador de expiração da sessão. Só com sessão viva — nas telas de
            login/primeiro-acesso não há o que contar, e um contador ali
            sugeriria que a sessão já começou. */}
        {session && (
          <SessionCountdown
            token={session.token}
            label={t('app.session_expires_in')}
            expiredLabel={t('app.session_expired')}
          />
        )}
        <nav aria-label={t('app.title')}>
          <a href="#dashboard" className={route.kind === 'dashboard' ? 'active' : ''}>
            {t('nav.dashboard')}
          </a>
          <a href="#cofre" className={route.kind === 'cofre' ? 'active' : ''}>
            {t('nav.cofre')}
          </a>
          <a href="#members" className={route.kind === 'members' ? 'active' : ''}>
            {t('nav.members')}
          </a>
          <a href="#audit" className={route.kind === 'audit' ? 'active' : ''}>
            {t('nav.audit')}
          </a>
          <a href="#substituicoes" className={route.kind === 'substituicoes' ? 'active' : ''}>
            {t('nav.substituicoes')}
          </a>
          {import.meta.env.DEV && (
            <a href="#dev-inspect" className={route.kind === 'dev-inspect' ? 'active' : ''}>
              🔧 dev
            </a>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => goto({ kind: 'change-password' })}
            data-testid="nav-change-password"
            style={{ marginLeft: 'auto' }}
          >
            {t('app.change_password')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleLogout()}
            style={{ marginLeft: 12 }}
          >
            {t('app.logout')}
          </Button>
        </nav>
      </header>

      <main id="main-content" className="app-main" tabIndex={-1}>
        <ErrorBoundary
          title={t('error_boundary.title')}
          message={t('error_boundary.message')}
          reloadLabel={t('error_boundary.reload')}
        >
          {/* `admin_master` explícito em 25/08. Ele JÁ passava aqui — por ter `cofre_holder` —,
              e passar por acidente é pior que não passar: quem lê o guard conclui que o
              dashboard é do T.I. e do cofre, e a conta do gestor entra por uma porta que não
              foi escolhida para ela. */}
          {route.kind === 'dashboard' && (
            <RoleGuard requires={['admin_ti', 'cofre_holder', 'admin_master']}>
              <DashboardPage />
            </RoleGuard>
          )}
          {/* 🔴 `admin_master` ENTRA (operador, 2026-08-19) — e esta era a TERCEIRA cópia.
              O gate do servidor foi aberto em 19/08 (`requireRole(['cofre_holder',
              'admin_master'])`) e o gestor continuou levando "sem acesso" na tela: a
              decisão vivia também aqui e no `ADMIN_ROLES` do login. O `nav`, por sua vez,
              NÃO é gateado — então o link aparecia e o clique batia nesta trava. Link
              visível + acesso negado é o pior par possível: parece bug de permissão do
              usuário, não decisão do sistema.
              Contadas as cópias antes de corrigir (§CLAUDE.md): servidor, este guard, e o
              allow-list de login. As três abertas no mesmo commit. */}
          {route.kind === 'cofre' && (
            <RoleGuard requires={['cofre_holder', 'admin_master', 'admin_ti']}>
              <CofrePage />
            </RoleGuard>
          )}
          {/* 🔴 `admin_master` nas TRÊS telas de T.I. (operador, 2026-08-25: "acesso a todas as
              abas do administrativo sem precisar do T.I").
              Contadas as cópias antes de mexer, como o commit de 19/08 acima ensinou: o guard
              do servidor (`adminApp.use('*', requireRole(...))` + os 3 sub-apps self-guarded),
              estes guards de tela, e o `ADMIN_ROLES` do login — que já admitia `admin_master`
              e era justamente por isso que o gestor ENTRAVA no SPA e levava 403 em tudo.
              `dev-inspect` fica FORA de propósito: é ferramenta de diagnóstico do T.I., só
              existe em `import.meta.env.DEV`, e não é uma das abas do administrativo. */}
          {route.kind === 'members' && (
            <RoleGuard requires={['admin_ti', 'admin_master']}>
              <MembersPage />
            </RoleGuard>
          )}
          {route.kind === 'audit' && (
            <RoleGuard requires={['admin_ti', 'admin_master']}>
              <AuditPage />
            </RoleGuard>
          )}
          {route.kind === 'substituicoes' && (
            <RoleGuard requires={['admin_ti', 'admin_master']}>
              <SubstituicoesPage />
            </RoleGuard>
          )}
          {import.meta.env.DEV && route.kind === 'dev-inspect' && DevInspectPage && (
            <RoleGuard requires={['admin_ti']}>
              <Suspense fallback={null}>
                <DevInspectPage />
              </Suspense>
            </RoleGuard>
          )}
          {route.kind === 'change-password' && (
            <ChangePasswordForm
              minLength={CHANGE_PASSWORD_MIN_LENGTH}
              labels={{
                title: t('app.change_password'),
                intro: t('change_password.intro'),
                currentLabel: t('change_password.current_label'),
                newLabel: t('change_password.new_label', { min: CHANGE_PASSWORD_MIN_LENGTH }),
                confirmLabel: t('change_password.confirm_label'),
                totpLabel: t('change_password.totp_label'),
                submit: t('change_password.submit'),
                submitting: t('change_password.submitting'),
                cancel: t('change_password.cancel'),
                doneTitle: t('change_password.done_title'),
                doneMessage: t('change_password.done_message'),
                doneButton: t('change_password.done_button'),
                errTooShort: t('change_password.errors.too_short', {
                  min: CHANGE_PASSWORD_MIN_LENGTH,
                }),
                errMismatch: t('change_password.errors.mismatch'),
                errSameAsCurrent: t('change_password.errors.same_password'),
                errInvalidTotp: t('change_password.errors.invalid_totp'),
                errInvalidCredentials: t('change_password.errors.invalid_credentials'),
                errNotAuthenticated: t('change_password.errors.not_authenticated'),
                errGeneric: t('change_password.errors.generic'),
              }}
              onSubmit={async (currentPassword, newPassword, totpCode) => {
                const res = await changePassword(currentPassword, newPassword, totpCode);
                if (res.ok) return { ok: true };
                if (res.error === 'invalid_credentials')
                  return { ok: false, error: 'invalid_credentials' };
                if (res.error === 'same_password') return { ok: false, error: 'same_password' };
                if (res.error === 'not_authenticated')
                  return { ok: false, error: 'not_authenticated' };
                return { ok: false, error: 'other' };
              }}
              onDone={() => goto({ kind: 'dashboard' })}
              onCancel={() => goto({ kind: 'dashboard' })}
            />
          )}
        </ErrorBoundary>
      </main>

      {session.user.policyAccepted === false && (
        <PolicyAcceptanceModal
          version={LGP_02_VERSION}
          labels={{
            title: t('policy_modal.title'),
            subtitle: t('policy_modal.subtitle'),
            versionLabel: t('policy_modal.version_label'),
            draftWarning: t('policy_modal.draft_warning'),
            scrollHint: t('policy_modal.scroll_hint'),
            checkbox: t('policy_modal.checkbox'),
            acceptButton: t('policy_modal.accept_button'),
            accepting: t('policy_modal.accepting'),
            logoutButton: t('policy_modal.logout_button'),
            errorNetwork: t('policy_modal.errors.network'),
            errorSubmit: t('policy_modal.errors.submit_failed'),
          }}
          onAccept={async () => {
            const res = await acceptPolicy();
            return res.ok
              ? { ok: true }
              : { ok: false, error: res.error === 'network' ? 'network' : 'submit' };
          }}
          onAccepted={() => setSession(getStoredSession())}
          onLogout={async () => {
            await logout();
            setSession(null);
            setRoute({ kind: 'login' });
            window.location.hash = '';
          }}
        />
      )}
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <SessionProvider>
      <AppShell />
    </SessionProvider>
  );
}
